import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
// material
import { Grid, Container, Stack, Typography, Card, CardContent, CardActions, Box, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// components
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { FormProvider, RHFTextField } from '../../components/hook-form';

// ----------------------------------------------------------------------

export default function GenerateExtractKeywords() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { pathname } = useLocation();

  const [results, setResults] = useState([]);

  useEffect(() => {
    const json = localStorage.getItem(pathname);
    const savedResults = JSON.parse(json);
    if (savedResults) {
      setResults(savedResults);
    }
  }, [pathname]);

  useEffect(() => {
    const json = JSON.stringify(results);
    localStorage.setItem(pathname, json);
  }, [pathname, results]);

  const { getAccessTokenSilently } = useAuth0();

  const formSchema = Yup.object().shape({
    text: Yup.string().required('Text field is required'),
  });

  const defaultValues = {
    text: '',
  };

  const methods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    const accessToken = await getAccessTokenSilently({
      audience: 'vrd-aigen/api',
    });

    const { text } = data;

    const opts = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const body = {
      text,
    };

    await axios
      .post(`${baseUrl}/generate/extract-keywords`, body, opts)
      .then((resp) => {
        const { data: response } = resp;
        setResults((prev) => [{ text, content: response.content, favorite: false }, ...prev]);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const setFavorite = (idx, favorite) => {
    setResults((prev) => prev.map((value, i) => (i === idx ? { ...value, favorite } : value)));
  };

  const deleteResult = (idx) => {
    setResults((prev) => prev.filter((_, i) => i !== idx));
  };

  const setValues = (item) => {
    setValue('text', item.text);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Page title="Extract Keywords">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Extract Keywords
          </Typography>
        </Stack>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <RHFTextField fullWidth multiline rows={10} name="text" label="Text" inputRef={formSchema} />
          </Stack>
          <br />
          <LoadingButton
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<Iconify icon="eva:play-circle-outline" />}
          >
            Generate
          </LoadingButton>
          <br />
        </FormProvider>
        <br />
        <Grid container spacing={2}>
          {results.map((result, index) => (
            <Grid key={`${index}-${result.content}`} item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ flex: '1 0 auto' }}>
                  <Typography variant="body" sx={{ flex: '1 0 auto', whiteSpace: 'pre-wrap' }}>
                    {result.content}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.text.length > 512 ? `${result.text.substring(0, 512)}...` : result.text}
                  </Typography>
                </CardContent>
                <CardActions sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1 }}>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setFavorite(index, !result.favorite);
                      }}
                    >
                      <Iconify icon={`eva:heart-${result.favorite ? 'fill' : 'outline'}`} />
                    </IconButton>
                    <IconButton onClick={() => setValues(result)}>
                      <Iconify icon="eva:edit-fill" />
                    </IconButton>
                  </Box>
                  <IconButton color="error" onClick={() => deleteResult(index)}>
                    <Iconify icon="eva:trash-2-fill" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Page>
  );
}
